import { useEffect, useState } from 'react'
import { useIsMutating } from '@tanstack/react-query'
import { ArrowUpCircle, Loader2 } from 'lucide-react'
import { desktop } from '@/lib/printTransport'
import { useDesktopUpdate } from '@/hooks/useDesktopUpdate'
import { useRestartBlocker } from '@/lib/restartGuard'

/**
 * Height reserved by the shells. Must match the `h-10` below and the
 * `--desktop-update-banner-h` fallback in index.css.
 */
const BANNER_HEIGHT = '2.5rem'

/**
 * "Update ready → Install & restart" strip for the desktop app (F-FE-N47).
 *
 * The updater already downloads new builds within ~30 minutes and applies them
 * on quit — but restaurant staff never quit the POS, so terminals sit on stale
 * builds for weeks. This is the missing cue and the safe way to act on it.
 *
 * Positioned `fixed` because `__root.tsx` cannot host an in-flow strip above
 * children that set their own `h-dvh`. It never occludes anything: while
 * mounted it publishes its height as `--desktop-update-banner-h`, and the
 * full-height shells (`routes/admin.tsx`, `routes/login.tsx`) subtract that from
 * their height and offset by it. In a browser the variable stays 0px.
 *
 * Renders nothing outside the desktop app, so the Railway web tenants are
 * untouched.
 */
export function DesktopUpdateBanner() {
  const bridge = desktop()
  const pending = useDesktopUpdate()
  const blocker = useRestartBlocker()
  const mutating = useIsMutating() > 0
  const [installing, setInstalling] = useState(false)

  const visible = Boolean(bridge && pending)

  // Reserve the strip's height in the layout for exactly as long as it's shown.
  useEffect(() => {
    if (!visible) return
    const root = document.documentElement
    root.style.setProperty('--desktop-update-banner-h', BANNER_HEIGHT)
    return () => {
      root.style.removeProperty('--desktop-update-banner-h')
    }
  }, [visible])

  if (!visible || !pending) return null

  // Over-broad on purpose: `useIsMutating` also catches harmless saves (a
  // setting, a table rename). Those resolve in milliseconds so the disable is
  // invisible, and the alternative — enumerating the dangerous mutations — is a
  // list that silently rots the first time someone adds a payment path.
  const reason = blocker ?? (mutating ? 'Something is still saving' : null)
  const blocked = reason !== null

  const onInstall = () => {
    if (blocked || installing) return
    setInstalling(true)
    void bridge?.installUpdate?.().catch(() => setInstalling(false))
  }

  return (
    <div
      role="status"
      className="fixed inset-x-0 top-0 z-[80] flex h-10 items-center justify-center gap-3 border-b border-emerald-800/40 bg-emerald-600 px-3 text-white shadow-sm"
    >
      <ArrowUpCircle className="h-4 w-4 shrink-0" aria-hidden />
      <span className="truncate text-sm font-medium">
        Update ready
        {pending.version ? <span className="opacity-80"> · v{pending.version}</span> : null}
      </span>

      {blocked ? (
        <span className="truncate text-xs opacity-90">{reason}</span>
      ) : null}

      <button
        type="button"
        onClick={onInstall}
        disabled={blocked || installing}
        title={blocked ? reason ?? undefined : 'Closes and reopens the POS (about 30 seconds)'}
        className="inline-flex h-7 shrink-0 items-center gap-1.5 rounded-md bg-white px-3 text-xs font-semibold text-emerald-700 shadow-sm transition-colors hover:bg-emerald-50 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {installing ? (
          <>
            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
            Restarting…
          </>
        ) : (
          'Install & restart'
        )}
      </button>
    </div>
  )
}
